import {
  MutateOptions,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import {
  useAuthQuery,
  useAuthMutation,
  UseQueryExtendedOptions,
} from "@rentablo/api";
import {
  GetSubscriptionsParams,
  PageableSubscriptionList,
  Subscription,
  UpdateSubscriptionParams,
  WizardData,
  WizardDataMutate,
} from "@api/v1/model";
import {
  createSubscription,
  downloadSubscription,
  getSubscriptions,
  loadMyLastIncompleteSubscription,
  loadSubscription,
  updateSubscription,
} from "@api/v1/methods";

import { startDateUTC } from "@helpers/datetime";
import { GET_SUBSCRIPTIONS } from "../../cacheKeys";
import { migrateSubscriptionWizardData } from "../methods/migrateSubscriptionWizardData";

const filterPasswordFromWizardData = <TParams extends UpdateSubscriptionParams>(
  attrs: TParams
): TParams => {
  if (!attrs.wizardData) return attrs;
  return {
    ...attrs,
    wizardData: {
      ...attrs.wizardData,
      values: { ...attrs.wizardData.values, password: undefined },
    },
  };
};

const createInitial = (
  token: string | undefined,
  { values, stepNumber, wizardType }: WizardData
) =>
  createSubscription(token, {
    type: "02_basic_flatrate",
    status: "incomplete",
    start: startDateUTC(),
    wizardData: { values, stepNumber, updatedAt: new Date(), wizardType },
  });

export const update = async (
  token: string | undefined,
  attrs: UpdateSubscriptionParams
): Promise<Subscription> =>
  updateSubscription(
    token,
    filterPasswordFromWizardData(attrs)
  ) as Promise<Subscription>;

const saveWizard = (
  token: string | undefined,
  attrs: {
    id: number;
    wizardData: WizardDataMutate;
  }
) => update(token, attrs);

export const useCreateInitialSubscription = () => {
  const queryClient = useQueryClient();
  return useAuthMutation(createInitial, {
    throwOnError: true,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptions-my-last-incomplete"],
      });
    },
  });
};

export const useUpdateSubscription = (
  // TODO: put axios error on place of unknown
  options: MutateOptions<
    Subscription,
    AxiosApiError,
    UpdateSubscriptionParams
  > = {}
) =>
  useAuthMutation(update, {
    throwOnError: true,
    ...options,
  });

export const useSaveWizard = () =>
  useAuthMutation(saveWizard, { throwOnError: true });

export const useSubscription = (id?: number) =>
  useAuthQuery<Subscription, AxiosApiError, Subscription>(
    ["subscriptions", id],
    ({ token }) => loadSubscription(token, id) as Promise<Subscription>,
    {
      enabled: !!id,
    }
  );

export const useMyLastIncompleteSubscription = (
  options?: UseQueryExtendedOptions<
    Subscription | null,
    AxiosApiError,
    Subscription | null
  >
) =>
  useAuthQuery<Subscription | null, AxiosApiError, Subscription | null>(
    ["subscriptions-my-last-incomplete"],
    ({ token }): Promise<Subscription> =>
      loadIncompleteSubscriptionWithDataMigration(
        token
      ) as Promise<Subscription>,
    options
  );

const loadIncompleteSubscriptionWithDataMigration = async (token: string) => {
  const subscription = await loadMyLastIncompleteSubscription(token);
  return migrateSubscriptionWizardData(subscription);
};

export const useGetSubscriptions = (
  params: GetSubscriptionsParams = {},
  options: UseQueryExtendedOptions<
    PageableSubscriptionList,
    AxiosApiError,
    PageableSubscriptionList
  > = { enabled: true }
) =>
  useAuthQuery(
    [GET_SUBSCRIPTIONS, params],
    ({ token }) => getSubscriptions(token, params),
    options
  );

export const useDownloadSubscription = (
  options?: UseMutationOptions<Blob, AxiosApiError, unknown>
) => useAuthMutation(downloadSubscription, options);
